import React from "react";
import { AlertTriangle, Upload } from "lucide-react";

const blockCls =
  "rounded-2xl border border-gray-300 dark:border-white/10 bg-bgSecondary dark:glass-effect p-4 space-y-3";
const headingCls =
  "text-xs font-semibold uppercase tracking-wide dark:text-gray-400 text-textSecondary";

/** "1m 40s", "50s", "2m" — whichever parts are non-zero. */
const formatPerQuestion = (seconds) => {
  const total = Math.round(seconds);
  const m = Math.floor(total / 60);
  const s = total % 60;
  if (!m) return `${s}s`;
  if (!s) return `${m}m`;
  return `${m}m ${s}s`;
};

/**
 * Right rail: what's broken, where everything is, what the settings actually mean,
 * and the save. Each block answers a question the others don't — the count and
 * derived figures live only in Summary so the blocks don't restate each other.
 *
 * Save sits at the foot of the rail, which is the foot of the page — nothing here
 * scrolls on its own, so a long navigator lengthens the page.
 */
const BuilderSidebar = ({
  questions = [],
  errors = {},
  config = {},
  onJumpTo,
  expandedId,
  onImportClick,
  saveLabel,
  saveBlockedReason,
  isSaving,
  isDirty,
  onSave,
}) => {
  const count = questions.length;
  const passPercent = config.pass_percent || 0;
  const timeLimit = config.time_limit_minutes || 0;

  const problemCount = questions.filter((q) => errors[q.id]).length;

  // A pass mark is only meaningful as a number of questions — 60% of 18 is "11 of
  // 18 right", which is the form you can actually sanity-check.
  const passCount = count ? Math.ceil((count * passPercent) / 100) : 0;
  const perQuestion = count && timeLimit ? (timeLimit * 60) / count : 0;

  return (
    <div className="w-full lg:w-72 shrink-0 flex flex-col gap-4">
      {/* This wrapper is the sticky's containing block, and it stops short of the
          button — otherwise the cards travel the full height of the rail and land on
          top of Save Set at the foot of a long page. lg:mt-8 lines this card stack's
          top up with question 1's — that's the height of QuestionListPanel's
          "Questions (n)" heading plus its gap, not an arbitrary offset. */}
      <div className="lg:flex-1 lg:pb-2 lg:mt-8">
        {/* The cards stick, not the rail: a sticky rail stretched to full page height
            would pin its own top and strand the save button far below the fold. */}
        <div className="space-y-4 lg:sticky lg:top-4">
          {count > 0 && (
            <div className={blockCls}>
              <h3 className={headingCls}>Import</h3>
              <p className="text-xs dark:text-gray-400 text-textSecondary">
                Upload a file to import your question set.
              </p>
              <button
                onClick={onImportClick}
                className="button w-full flex items-center justify-center gap-2 text-sm"
              >
                <Upload size={14} />
                Import questions
              </button>
            </div>
          )}

          {count > 0 && (
            <div className={blockCls}>
              <div className="flex items-center justify-between gap-2">
                <h3 className={headingCls}>Navigator</h3>
                {problemCount > 0 && (
                  <span className="flex items-center gap-1 text-[11px] font-medium text-red-400">
                    <AlertTriangle size={11} />
                    {problemCount} to fix
                  </span>
                )}
              </div>
              <div className="grid grid-cols-7 gap-1">
                {questions.map((q, i) => {
                  const isActive = q.id === expandedId;
                  const hasError = !!errors[q.id];
                  const summary = q.text || "Untitled question";
                  return (
                    <button
                      key={q.id}
                      onClick={() => onJumpTo(q.id)}
                      // The tile is the only place in the rail that names what's wrong now
                      // that the problems card is gone.
                      title={hasError ? `${summary} — ${errors[q.id]}` : summary}
                      // The current question gets the same gradient-border-plus-gradient-text
                      // treatment as the sequences wizard's Knock-Out chip — every other tile
                      // keeps a standing gradient ring so all of them read as targets, not
                      // just the one under the pointer. A broken question outranks that: red
                      // either way, solid rather than tinted when it's also the current one.
                      className={`aspect-square rounded-lg text-xs font-medium flex items-center justify-center transition-colors ${
                        hasError
                          ? isActive
                            ? "border border-red-500 bg-red-500 text-white"
                            : "border border-red-400 bg-red-500/15 text-red-400"
                          : isActive
                          ? "knockout-tile"
                          : "gradient-ring always border border-gray-300 dark:border-white/15 dark:text-gray-400 text-textSecondary"
                      }`}
                    >
                      {/* knockout-tile-text is gradient-clipped transparent text, so it only
                          goes with the gradient tile — on red it would render invisible. */}
                      {isActive && !hasError ? (
                        <span className="knockout-tile-text">{i + 1}</span>
                      ) : (
                        i + 1
                      )}
                    </button>
                  );
                })}
              </div>
            </div>
          )}

          {/* Every figure here is derived from the questions, so with none there is
              nothing to summarise. Rows whose input is blank are dropped rather than
              shown as "—" — an absent row reads as "not set", a dash reads as
              "set to nothing". */}
          {count > 0 && (
            <div className={blockCls}>
              <h3 className={headingCls}>Summary</h3>
              <dl className="space-y-2 text-xs">
                <div className="flex items-center justify-between">
                  <dt className="dark:text-gray-400 text-textSecondary">Questions</dt>
                  <dd className="dark:text-white text-textPrimary font-medium">{count}</dd>
                </div>
                {timeLimit > 0 && (
                  <>
                    <div className="flex items-center justify-between">
                      <dt className="dark:text-gray-400 text-textSecondary">Time limit</dt>
                      <dd className="dark:text-white text-textPrimary font-medium">
                        {timeLimit} min
                      </dd>
                    </div>
                    <div className="flex items-center justify-between">
                      <dt className="dark:text-gray-400 text-textSecondary">Time per Q (avg)</dt>
                      <dd className="dark:text-white text-textPrimary font-medium">
                        ≈{formatPerQuestion(perQuestion)}
                      </dd>
                    </div>
                  </>
                )}
                {passPercent > 0 && (
                  <div className="flex items-center justify-between pt-2 border-t border-white/10">
                    <dt className="dark:text-gray-400 text-textSecondary">Pass threshold</dt>
                    <dd className="dark:text-white text-textPrimary font-medium">
                      {passCount} of {count}
                    </dd>
                  </div>
                )}
              </dl>
            </div>
          )}
        </div>
      </div>

      {/* Foot of the rail, which is the foot of the page — no pinning, it scrolls
          away like anything else. */}
      <div className="shrink-0 flex justify-end">
        {/* A disabled button receives no pointer events, so its own title never fires. */}
        <span className="inline-flex" title={saveBlockedReason || undefined}>
          <button
            onClick={onSave}
            disabled={isSaving || !isDirty || !!saveBlockedReason}
            className="button disabled:opacity-50"
          >
            {isSaving ? "Saving…" : saveLabel}
          </button>
        </span>
      </div>
    </div>
  );
};

export default BuilderSidebar;
